import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, UserRole } from '../auth/AuthContext';
import { Logo } from './Logo';
import { Avatar } from './Avatar';
import { useTheme } from '../theme/ThemeContext';

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
    isActive
      ? 'bg-brand-500 text-white shadow-sm'
      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
  }`;

const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Administrateur',
  client: 'Client',
  deliverer: 'Livreur',
  business: 'Entreprise'
};

function navItems(role: UserRole): Array<{ to: string; label: string }> {
  const items = [
    { to: '/orders', label: 'Mes courses' },
    { to: '/historique', label: 'Historique' },
    { to: '/profil', label: 'Mon profil' }
  ];
  if (role === 'client' || role === 'business' || role === 'admin') {
    items.splice(1, 0, { to: '/orders/new', label: 'Nouvelle course' });
  }
  if (role === 'deliverer' || role === 'admin') {
    items.push({ to: '/deliverer', label: 'Ma tournée' });
  }
  items.push({ to: '/kyc', label: 'Mes documents' });
  if (role === 'admin') {
    items.push(
      { to: '/admin/analytics', label: 'Chiffres' },
      { to: '/admin/observability', label: 'Santé' },
      { to: '/admin/kyc', label: 'Vérifier les documents' }
    );
  }
  return items;
}

function ThemeToggle({ className = '' }: { className?: string }) {
  const { night, toggle } = useTheme();
  return (
    <button
      type="button"
      className={`btn-ghost whitespace-nowrap !px-3 !py-2 ${className}`}
      aria-pressed={night}
      onClick={toggle}
    >
      {night ? 'Mode jour' : 'Mode nuit'}
    </button>
  );
}

export const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isHome = location.pathname === '/';

  const handleLogout = () => {
    logout();
    setMobileOpen(false);
    navigate('/', { replace: true });
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 surface-header">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
          <Link
            to="/"
            className="group flex shrink-0 items-center gap-2.5 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            onClick={closeMobile}
          >
            <Logo />
          </Link>

          <nav className="hidden min-w-0 flex-1 items-center justify-end gap-1 md:flex">
            {user &&
              navItems(user.role).map((item) => (
                <NavLink key={item.to} to={item.to} className={navLinkClass}>
                  {item.label}
                </NavLink>
              ))}
            {!user && (
              <>
                <NavLink to="/login" className={navLinkClass}>
                  Connexion
                </NavLink>
                <NavLink to="/register" className={navLinkClass}>
                  Inscription
                </NavLink>
              </>
            )}
          </nav>

          <div className="hidden shrink-0 items-center gap-2 md:flex">
            <ThemeToggle />
          </div>

          {user && (
            <div className="hidden shrink-0 items-center gap-3 border-l border-slate-200 pl-3 md:flex">
              <Avatar
                size="sm"
                profilePictureUrl={user.profilePictureUrl}
                firstName={user.firstName}
                lastName={user.lastName}
              />
              <div className="max-w-[9.5rem] text-right leading-tight">
                <div className="truncate text-sm font-medium text-slate-900">
                  {user.firstName} {user.lastName}
                </div>
                <div className="text-[11px] text-slate-500">{ROLE_LABELS[user.role]}</div>
              </div>
              <button type="button" className="btn-ghost whitespace-nowrap !px-3 !py-2" onClick={handleLogout}>
                Déconnexion
              </button>
            </div>
          )}

          <ThemeToggle className="md:hidden" />

          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 shadow-sm md:hidden"
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
            onClick={() => setMobileOpen((o) => !o)}
          >
            <span className="sr-only">Menu</span>
            {mobileOpen ? (
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {mobileOpen && (
          <div
            id="mobile-nav"
            className="border-t border-slate-200/80 bg-white/95 px-4 py-3 shadow-soft md:hidden animate-fade-in"
          >
            <div className="flex flex-col gap-1">
              <ThemeToggle className="justify-start" />
              {user && (
                <>
                  {navItems(user.role).map((item) => (
                    <NavLink key={item.to} to={item.to} className={navLinkClass} onClick={closeMobile}>
                      {item.label}
                    </NavLink>
                  ))}
                  <p className="mt-2 px-3 text-xs text-slate-500">
                    {user.firstName} {user.lastName} · {ROLE_LABELS[user.role]}
                  </p>
                  <button type="button" className="btn-ghost mt-1 justify-start" onClick={handleLogout}>
                    Déconnexion
                  </button>
                </>
              )}
              {!user && (
                <>
                  <NavLink to="/login" className={navLinkClass} onClick={closeMobile}>
                    Connexion
                  </NavLink>
                  <NavLink to="/register" className={navLinkClass} onClick={closeMobile}>
                    Inscription
                  </NavLink>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        {isHome ? (
          <Outlet />
        ) : (
          <div className="mx-auto max-w-6xl px-4 py-8 sm:py-10">
            <Outlet />
          </div>
        )}
      </main>

      {!isHome && (
        <footer className="mt-auto border-t border-slate-200/80 bg-white/60 py-6 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} nafola
        </footer>
      )}
    </div>
  );
};
